/**
 * Données démo isolées — jamais mélangées aux enregistrements robot.
 */
import {
  DEFAULT_ANNUAL_FEE_CENTS,
  DEFAULT_CURRENCY,
  HEALTH_FORM_VERSION_ADULT,
  HEALTH_FORM_VERSION_MINOR,
  MEMBERSHIP_STATUSES,
  PAYMENT_STATUSES,
  REGISTRATION_KIND,
  BLACK_DRAGON_STATUSES,
  BD_CONTENT_VISIBILITY,
  TEMP_PERIOD_POLICY,
  TEMP_SENSEI_HEALTH_POLICY,
  TEMP_BD_APPROVER_POLICY,
} from "./membershipConstants.js";
import { SEED_STUDENT_MARCEL_ID, SEED_STUDENT_DEMO_CHILD_ID } from "@/data/phase1InitialSeed046.js";

export function defaultMembershipSettings() {
  return {
    annualFeeCents: DEFAULT_ANNUAL_FEE_CENTS,
    currency: DEFAULT_CURRENCY,
    periodType: "calendar_year",
    periodPolicy: TEMP_PERIOD_POLICY,
    healthFormVersionAdult: HEALTH_FORM_VERSION_ADULT,
    healthFormVersionMinor: HEALTH_FORM_VERSION_MINOR,
    allowBlackDragonSelfRequest: true,
    blackDragonApproverRoles: ["hanshi", "admin"],
    bdApproverPolicy: TEMP_BD_APPROVER_POLICY,
    senseiCanViewHealth: false,
    senseiHealthPolicy: TEMP_SENSEI_HEALTH_POLICY,
  };
}

const DEMO_HEALTH_ADULT = {
  hasMedicalConditions: "no",
  medicalConditionsDetails: "",
  hasAllergies: "no",
  allergiesDetails: "",
  hasMedications: "no",
  medicationsDetails: "",
  activityRestrictions: "",
  physicianName: "",
  physicianPhone: "",
  emergencyCareConsent: true,
  accuracyAttestation: true,
  immunizationUpToDate: "",
  parentalHealthAuthority: false,
};

export function buildDemoMembershipSeed() {
  return [
    {
      id: "ms-demo-marcel",
      kind: REGISTRATION_KIND.ADULT,
      status: MEMBERSHIP_STATUSES.ACTIVE,
      demo: true,
      origin: "seed-demo",
      studentId: SEED_STUDENT_MARCEL_ID,
      identity: {
        firstName: "Marcel",
        lastName: "Charette",
        birthDate: "1975-04-12",
        address: "Adresse à confirmer",
        city: "Ville à confirmer",
        province: "Québec",
        postalCode: "G1A 1A1",
        phone: "418-555-0101",
        email: "etudiant",
        dojoId: "demo-dojo-01",
        instructorId: "demo-sensei-placeholder",
      },
      emergency: { name: "Natalie Lavigne", relation: "Conjointe", phone: "418-555-0102" },
      parent: null,
      health: {
        version: HEALTH_FORM_VERSION_ADULT,
        completedAt: "2026-01-10T12:00:00.000Z",
        completedBy: "etudiant",
        answers: { ...DEMO_HEALTH_ADULT },
      },
      consents: { privacy: true, healthAccuracy: true, photoOptional: false, parentalAuthority: true },
      payment: {
        status: PAYMENT_STATUSES.CONFIRMED,
        amountCents: DEFAULT_ANNUAL_FEE_CENTS,
        currency: DEFAULT_CURRENCY,
        method: "sandbox",
        provider: "sandbox",
        transactionId: "sbx-demo-marcel",
        paidAt: "2026-01-10T12:05:00.000Z",
        testMode: true,
      },
      periodStart: "2026-01-10",
      periodEnd: "2026-12-31",
      periodYear: 2026,
      activatedAt: "2026-01-10T12:05:00.000Z",
      memberPermissionsGranted: true,
      adminPermissionsGranted: false,
      blackDragon: { status: BLACK_DRAGON_STATUSES.NOT_MEMBER, history: [] },
      createdAt: "2026-01-10T11:00:00.000Z",
      updatedAt: "2026-01-10T12:05:00.000Z",
    },
    {
      id: "ms-demo-child",
      kind: REGISTRATION_KIND.MINOR,
      status: MEMBERSHIP_STATUSES.ACTIVE,
      demo: true,
      origin: "seed-demo",
      studentId: SEED_STUDENT_DEMO_CHILD_ID,
      identity: {
        firstName: "Enfant",
        lastName: "Démo",
        birthDate: "2016-06-01",
        address: "Adresse à confirmer",
        city: "Ville à confirmer",
        province: "Québec",
        postalCode: "G1A 1A1",
        phone: "418-555-0199",
        email: "enfant.demo@example.local",
        dojoId: "demo-dojo-01",
        instructorId: "demo-sensei-placeholder",
      },
      emergency: { name: "Parent démo", relation: "Parent", phone: "418-555-0198" },
      parent: {
        firstName: "Parent",
        lastName: "Démo",
        relation: "Parent",
        phone: "418-555-0198",
        email: "parent",
        address: "Adresse à confirmer",
        city: "Ville à confirmer",
        province: "Québec",
        postalCode: "G1A 1A1",
      },
      health: {
        version: HEALTH_FORM_VERSION_MINOR,
        completedAt: "2026-01-12T12:00:00.000Z",
        completedBy: "parent",
        answers: { ...DEMO_HEALTH_ADULT, parentalHealthAuthority: true, immunizationUpToDate: "yes" },
      },
      consents: { privacy: true, healthAccuracy: true, photoOptional: false, parentalAuthority: true },
      payment: {
        status: PAYMENT_STATUSES.CONFIRMED,
        amountCents: DEFAULT_ANNUAL_FEE_CENTS,
        currency: DEFAULT_CURRENCY,
        method: "sandbox",
        provider: "sandbox",
        transactionId: "sbx-demo-child",
        paidAt: "2026-01-12T12:05:00.000Z",
        testMode: true,
      },
      periodStart: "2026-01-12",
      periodEnd: "2026-12-31",
      periodYear: 2026,
      activatedAt: "2026-01-12T12:05:00.000Z",
      memberPermissionsGranted: true,
      adminPermissionsGranted: false,
      blackDragon: { status: BLACK_DRAGON_STATUSES.NOT_MEMBER, history: [] },
      createdAt: "2026-01-12T11:00:00.000Z",
      updatedAt: "2026-01-12T12:05:00.000Z",
    },
    {
      id: "ms-demo-incomplete",
      kind: REGISTRATION_KIND.ADULT,
      status: MEMBERSHIP_STATUSES.HEALTH_PENDING,
      demo: true,
      origin: "seed-demo",
      studentId: null,
      identity: {
        firstName: "Alex",
        lastName: "Tremblay",
        birthDate: "1990-03-03",
        address: "12 rue Démo",
        city: "Québec",
        province: "Québec",
        postalCode: "G1K 1K1",
        phone: "418-555-0110",
        email: "alex.demo@example.local",
        dojoId: "demo-dojo-01",
        instructorId: "demo-sensei-placeholder",
      },
      emergency: { name: "Sam Tremblay", relation: "Frère", phone: "418-555-0111" },
      parent: null,
      health: { version: HEALTH_FORM_VERSION_ADULT, completedAt: null, completedBy: null, answers: {} },
      consents: { privacy: true, healthAccuracy: false, photoOptional: false, parentalAuthority: true },
      payment: { status: PAYMENT_STATUSES.NONE, amountCents: DEFAULT_ANNUAL_FEE_CENTS, currency: DEFAULT_CURRENCY, testMode: true },
      blackDragon: { status: BLACK_DRAGON_STATUSES.NOT_MEMBER, history: [] },
      memberPermissionsGranted: false,
      adminPermissionsGranted: false,
      createdAt: "2026-08-01T10:00:00.000Z",
      updatedAt: "2026-08-01T10:00:00.000Z",
    },
  ];
}

export function buildDemoCredentialsSeed() {
  return [
    {
      email: "etudiant",
      password: "demo",
      role: "student",
      membershipId: "ms-demo-marcel",
      linkedMembershipIds: ["ms-demo-marcel"],
      name: "Marcel Charette",
      dojoId: "demo-dojo-01",
      studentId: SEED_STUDENT_MARCEL_ID,
      origin: "seed-demo",
      demo: true,
    },
    {
      email: "parent",
      password: "demo",
      role: "parent",
      membershipId: "ms-demo-child",
      linkedMembershipIds: ["ms-demo-child"],
      name: "Parent démo",
      dojoId: "demo-dojo-01",
      studentId: SEED_STUDENT_DEMO_CHILD_ID,
      origin: "seed-demo",
      demo: true,
    },
  ];
}

export function buildDemoBlackDragonContent() {
  return [
    {
      id: "bd-content-public",
      visibility: BD_CONTENT_VISIBILITY.PUBLIC,
      titleFr: "Black Dragon — présentation publique",
      titleEn: "Black Dragon — public introduction",
      bodyFr: "Black Dragon est une adhésion privée supplémentaire, distincte du statut membre Shotokan AAMC.",
      bodyEn: "Black Dragon is an additional private membership, distinct from Shotokan AAMC member status.",
      demo: true,
    },
    {
      id: "bd-content-members",
      visibility: BD_CONTENT_VISIBILITY.SHOTOKAN_MEMBERS,
      titleFr: "[DÉMO] Nouvelle réservée aux membres Shotokan",
      titleEn: "[DEMO] News reserved for Shotokan members",
      bodyFr: "Information visible aux membres actifs Shotokan AAMC.",
      bodyEn: "Information visible to active Shotokan AAMC members.",
      demo: true,
    },
    {
      id: "bd-content-private",
      visibility: BD_CONTENT_VISIBILITY.BLACK_DRAGON,
      titleFr: "[DÉMO] Document privé Black Dragon",
      titleEn: "[DEMO] Private Black Dragon document",
      bodyFr: "Contenu réservé aux membres Black Dragon actifs.",
      bodyEn: "Content reserved for active Black Dragon members.",
      demo: true,
    },
    {
      id: "bd-content-admin",
      visibility: BD_CONTENT_VISIBILITY.ADMIN,
      titleFr: "[DÉMO] Note d'administration Black Dragon",
      titleEn: "[DEMO] Black Dragon administration note",
      bodyFr: "Gestion interne — administration uniquement.",
      bodyEn: "Internal management — administration only.",
      demo: true,
    },
  ];
}
